import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../utils/common";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        info: "bg-blue-600 text-white hover:bg-blue-700",
        success: "bg-green-600 text-white hover:bg-green-700",
        warning: "bg-yellow-500 text-white hover:bg-yellow-600",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
      rounded: {
        default: "rounded-md",
        none: "rounded-none",
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        full: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      rounded: "default",
    },
  }
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "rounded">,
    Omit<VariantProps<typeof buttonVariants>, "rounded"> {
  asChild?: boolean;
  primary?: boolean;
  secondary?: boolean;
  destructive?: boolean;
  success?: boolean;
  warning?: boolean;
  info?: boolean;
  ghost?: boolean;
  outline?: boolean;
  outlinePrimary?: boolean;
  link?: boolean;
  block?: boolean;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "info" | "success" | "warning";
  size?: "default" | "sm" | "lg" | "icon";
  rounded?: boolean | "default" | "none" | "sm" | "md" | "lg" | "full";
  danger?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      asChild = false,
      primary,
      secondary,
      destructive,
      success,
      warning,
      info,
      ghost,
      outline,
      outlinePrimary,
      link,
      block,
      danger,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    // Map legacy boolean props to variants
    let effectiveVariant = variant;
    if (!effectiveVariant) {
      if (primary) effectiveVariant = "default";
      else if (secondary) effectiveVariant = "secondary";
      else if (destructive || danger) effectiveVariant = "destructive";
      else if (success) effectiveVariant = "success";
      else if (warning) effectiveVariant = "warning";
      else if (info) effectiveVariant = "info";
      else if (ghost) effectiveVariant = "ghost";
      else if (outline) effectiveVariant = "outline";
      else if (outlinePrimary) effectiveVariant = "outline";
      else if (link) effectiveVariant = "link";
    }

    let effectiveRounded = rounded;
    if (rounded === true) {
      effectiveRounded = "full";
    } else if (rounded === false) {
      effectiveRounded = "none";
    }

    return (
      <Comp
        className={cn(
          buttonVariants({
            variant: effectiveVariant,
            size,
            rounded: effectiveRounded as any,
            className,
          }),
          block && "w-full"
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
export default Button;
